import { useCallback } from 'react';

import { useItemsOffset } from 'bundles/cds-labs/components/OverflowCarousel/OverflowCarouselProvider';

const useUpdateOverflowCarouselState = ({
  setShowPreviousButton,
  setShowNextButton,
  isMobileBrowser,
  setItemsInView,
}: {
  setShowPreviousButton: (show: boolean) => void;
  setShowNextButton: (show: boolean) => void;
  isMobileBrowser: boolean;
  setItemsInView?: (items: string[]) => void;
}) => {
  const getItemsOffset = useItemsOffset();

  return useCallback<(carouselRefCurrent: HTMLDivElement | null, contentRefCurrent: HTMLDivElement | null) => void>(
    (carouselRefCurrent, contentRefCurrent) => {
      if (!contentRefCurrent || !carouselRefCurrent) {
        return;
      }

      // if the user is on a mobile browser, they should be able to swipe to see hidden content, so don't show the buttons
      if (isMobileBrowser) {
        return;
      }

      if (Math.abs(carouselRefCurrent.scrollLeft) > 0) {
        setShowPreviousButton(true);
      } else {
        setShowPreviousButton(false);
      }

      if (
        Math.ceil(Math.abs(carouselRefCurrent.scrollLeft) + carouselRefCurrent.offsetWidth) >=
        Math.floor(contentRefCurrent.offsetWidth)
      ) {
        setShowNextButton(false);
      } else {
        setShowNextButton(true);
      }

      // if setItemsInView is not provided, no need to check for items in view
      if (!setItemsInView) {
        return;
      }

      const itemsInView: string[] = [];
      const leftThreshold = carouselRefCurrent.scrollLeft;
      const rightThreshold = leftThreshold + carouselRefCurrent.offsetWidth;

      const itemsOffset = getItemsOffset?.() ?? new Map<string, { offsetLeft: number; offsetWidth: number }>();
      const iterator = itemsOffset?.entries();
      let currentEntry = iterator?.next();

      while (!currentEntry?.done) {
        const [key, value] = currentEntry?.value;
        if (value.offsetLeft + value.offsetWidth <= rightThreshold && value.offsetLeft >= leftThreshold) {
          itemsInView.push(key);
        }
        currentEntry = iterator?.next();
      }

      if (itemsInView.length > 0) {
        setItemsInView(itemsInView);
      }
    },

    [setShowNextButton, setShowPreviousButton, isMobileBrowser, getItemsOffset, setItemsInView]
  );
};

export default useUpdateOverflowCarouselState;
